import { Point, MultiPoint } from 'ol/geom';
import i18n from '../../../i18n';
import { SELECTION_TYPES, TAB_STATUS, MAX_RESULTS_RELATION } from '../../../constants';
import { getCombinedOlId } from '../../../services/theme';
import { joinFeatures } from '../../../services/map/feature';
import { layerFeaturesCanBePaged, displayThemeAttribute } from '../../../services/map/feature/selection';

export const actions = {
    async findSpatialRelation({ getters, commit, dispatch }, { relation, features, tabName }) {
        commit('NEDGLOBE_MOBILE_DISPLAY_SELECTION_START');

        // store the results from all the selection tabs except the targetLayer tab
        const referenceResults = getters.getSelectionData.results.filter(
            (resultSet) => resultSet.layer.data.id !== relation.targetThemeLayerId);

        // find the id's for the target theme and layer
        const themeId = relation.targetThemeId;
        const layerId = getters.isMobile ? getCombinedOlId(themeId, relation.targetThemeLayerId) : relation.targetThemeLayerId;
        // in web there is allready a loaded (web)theme
        if (getters.isMobile) {
            // make sure the theme and its layers where the search is executed upon are loaded
            // it is needed for the service url, etc
            await dispatch('loadTheme', { themeId });
        }
        const layer = getters.getLayerById(layerId);

        // get polygon when point/multipoint
        const joinedFeatures = features.map((feature) => {
            const startGeometry = feature.getGeometry();
            if (startGeometry instanceof Point || startGeometry instanceof MultiPoint) {
                return joinFeatures([feature], 1);
            }
            return feature;
        });

        const geometry = (joinedFeatures.length === 1) ? joinedFeatures[0].getGeometry() : joinFeatures(joinedFeatures, 1).getGeometry();
        const selectionOptions = { geometry, layers: [layer], within: false };
        try {
            // perform the search via filters
            const foundResults = await dispatch('getLayerSelection', selectionOptions);
            const result = foundResults?.[0];
            // set focus property so the correct tab can be focussed in the resultatentabel
            result.meta.focus = true;
            // process the results
            if (!result || result.features?.length === 0) {
                const message = 'noResultsFound';
                commit('UPDATE_LOADING_OBJECT_DATA', {
                    title: tabName,
                    order: relation.order,
                    data: {
                        items: [],
                        fields: []
                    },
                    count: 0,
                    status: TAB_STATUS.READY,
                    message: i18n.t(message)
                });

                commit('ADD_STATUS_MESSAGE', {
                    message: i18n.t(message),
                    variant: 'warning',
                    autoHideDelay: 5000
                });
            } else if (result.limitedResults) {
                const message = 'tooManyResultsFound';
                commit('UPDATE_LOADING_OBJECT_DATA', {
                    title: tabName,
                    order: relation.order,
                    data: {
                        items: [],
                        fields: []
                    },
                    count: 0,
                    status: TAB_STATUS.READY,
                    message: i18n.t(message)
                });
                commit('ADD_STATUS_MESSAGE', {
                    message: i18n.t(message),
                    variant: 'warning',
                    autoHideDelay: 5000
                });
            } else {
                const results = [result, ...referenceResults];
                commit('ADD_SELECTION', { location: null, results, type: SELECTION_TYPES.RELATION });
            }

        } catch (err) {
            commit('UPDATE_LOADING_OBJECT_DATA', {
                title: tabName,
                order: relation.order,
                data: {
                    items: [],
                    fields: []
                },
                count: 0,
                status: TAB_STATUS.ERROR,
                message: i18n.t('errorWhenSearching')
            });
            commit('ADD_STATUS_MESSAGE', {
                message: i18n.t('errorWhenSearching'),
                variant: 'danger',
                autoHideDelay: 5000
            });
        }
        commit('NEDGLOBE_MOBILE_DISPLAY_SELECTION_END');
    },
    async findDataRelation({ getters, commit, dispatch, state },
        { relation, filters, multipleFilters = false, showSelectionOnMap = true, tabName }) {
        let results = [];

        if (showSelectionOnMap) {
            commit('NEDGLOBE_MOBILE_DISPLAY_SELECTION_START');
        }
        // store the results from all the selection tabs except the targetLayer tab
        const referenceResults = getters.getSelectionData?.results.filter(
            (resultSet) => resultSet.layer.data.id !== relation.targetThemeLayerId) ?? [];

        // search the maximum area for the organization
        let bbox = getters.getOrganizationBBox;

        // No need for a bbox when there is filter
        if (filters) {
            bbox = null;
        }
        // find the id's for the target theme and layer
        const themeId = relation.targetThemeId;
        const layerId = getters.isMobile ? getCombinedOlId(themeId, relation.targetThemeLayerId) : relation.targetThemeLayerId;
        try {
            // in web there is allready a loaded (web)theme
            if (getters.isMobile) {
                // make sure the theme and its layers where the search is executed upon are loaded
                // it is needed for the service url, etc
                await dispatch('loadTheme', { themeId });
            }
            const layer = getters.getLayerById(layerId);
            // perform the search via filters
            const features = await dispatch('searchFeaturesOnLayerWithCustomFilters', { bbox, layerId, filters, multipleFilters });
            // process the results
            if (!features || features.length === 0) {
                const message = 'noResultsFound';

                commit('UPDATE_LOADING_OBJECT_DATA', {
                    title: tabName,
                    order: relation.order,
                    data: {
                        items: [],
                        fields: []
                    },
                    count: 0,
                    status: TAB_STATUS.READY,
                    message: i18n.t(message)
                });
                commit('ADD_STATUS_MESSAGE', {
                    message: i18n.t(message),
                    variant: 'warning',
                    autoHideDelay: 5000
                });
            } else if (!layerFeaturesCanBePaged(layer) && features.length > state.userData.maxFeatures) {
                const message = 'tooManyResultsFound';

                commit('UPDATE_LOADING_OBJECT_DATA', {
                    title: tabName,
                    order: relation.order,
                    data: {
                        items: [],
                        fields: []
                    },
                    count: 0,
                    status: TAB_STATUS.READY,
                    message: i18n.t(message)
                });
                commit('ADD_STATUS_MESSAGE', {
                    message: i18n.t(message),
                    variant: 'warning',
                    autoHideDelay: 5000
                });
            } else {
                const theme = getters.getThemeById(themeId);
                const meta = {
                    type: 'theme',
                    theme,
                    focus: true
                };
                results = [{
                    layer,
                    features: features,
                    meta,
                    limitedResults: false
                }, ...referenceResults];

                if (showSelectionOnMap) {
                    commit('ADD_SELECTION', { location: null, results, type: SELECTION_TYPES.RELATION });
                }
            }
        } catch (err) {
            commit('UPDATE_LOADING_OBJECT_DATA', {
                title: tabName,
                order: relation.order,
                data: {
                    items: [],
                    fields: []
                },
                count: 0,
                status: TAB_STATUS.ERROR,
                message: i18n.t('errorWhenSearching')
            });
            commit('ADD_STATUS_MESSAGE', {
                message: i18n.t('errorWhenSearching'),
                variant: 'danger',
                autoHideDelay: 5000
            });
        }

        if (showSelectionOnMap) {
            commit('NEDGLOBE_MOBILE_DISPLAY_SELECTION_END');
        }

        return results;
    },
    async findThemeLayer2TableRelation({ getters, commit, dispatch }, { relation, objectData, features, computedStyles }) {
        // show related objects on map
        commit('ADD_SELECTION_OBJECT_DATA', {
            title: relation.name,
            results: {
                features,
                computedStyles,
            }
        });

        // Clear possible results for this title and open new tab with status Loading
        dispatch('initTabObjectSearch', { title: relation.name });

        try {
            const tableResult = await dispatch('useApiData', {
                name: getters.isMobile ? 'GetTableSearchResult' : 'GetPublicTableSearchResult',
                parameters: { Id: relation.targetThemeLayerId }
            });
            const tableSettings = Object.assign({}, tableResult?.data);

            const relatedTableData = await Promise.all(
                objectData.map((data) =>
                    dispatch('useApiData', {
                        name: getters.isMobile ? 'GetRelatedTableData' : 'GetPublicRelatedTableData',
                        parameters: { Id: relation.id },
                        data
                    })
                ));

            const tableData = relatedTableData
                .map((result, index) => {
                    const data = result.data && result.data.length > 0 ? JSON.parse(result.data) : [];
                    return {
                        data,
                        features: data.map(() => {
                            return features[index];
                        })
                    };
                })
                .filter(({ data }) => data.length > 0);

            let fields = [];
            let maxResultsExceeded = false;
            let items = tableData.flatMap(({ data }) => data);
            const relatedFeatures = tableData.flatMap(({ features }) => features);

            if (items.length > 0) {
                maxResultsExceeded = items.length > MAX_RESULTS_RELATION;
                if (maxResultsExceeded) {
                    items = items.slice(0, MAX_RESULTS_RELATION);
                }
                items.forEach((item, index) => {
                    // provide a unique ID column to avoid conflicts with the ID column in the data
                    item.nedGlobeRowId = index;
                });
                // Return attributes visable in table and in detail
                fields = tableSettings.tableSearchResultFields
                    .filter(attribute => {
                        return attribute.displayInTable || attribute.displayInDetail;
                    })
                    .map(attribute => (
                        {
                            ...attribute,
                            key: attribute.columnName,
                            label: attribute.alias,
                            sortable: true,
                        }
                    ))
                    .sort((a, b) => a.order - b.order);
            }
            commit('UPDATE_LOADING_OBJECT_DATA', {
                title: relation.name,
                order: relation.order,
                data: {
                    items,
                    fields,
                    features: relatedFeatures,
                    selectable: true,
                    tableSearchResult: relation.toTableSearchResultId
                },
                message: maxResultsExceeded ? i18n.t('relations.maxResultsExceeded', [MAX_RESULTS_RELATION]) : '',
                count: items.length,
                exportable: true,
                download: true,
                printable: true,
                status: TAB_STATUS.READY
            });
        } catch (err) {
            commit('UPDATE_LOADING_OBJECT_DATA', {
                title: relation.name,
                order: relation.order,
                data: {
                    items: [],
                    fields: []
                },
                count: 0,
                status: TAB_STATUS.ERROR,
                message: err.message
            });
        }
    },
    async findReversedTableRelation({ getters, commit, dispatch }, { title, relation, data, isAdvancedSearch = false }) {
        // fill relation parameters
        const layer = getters.getLayerByOriginalId(relation.fromThemeLayerId);
        relation.targetThemeId = layer?.data?.themeId;
        relation.targetThemeLayerId = relation.fromThemeLayerId;

        // construct array with relations items and its data
        const objectData = data.map((object) => {
            if (relation.settings.relationTable) {
                return relation.settings.itemsSecondRelation.map((secondRelationItem) => ({
                    caseSensitive: secondRelationItem.caseSensitive,
                    dataType: secondRelationItem.dataType,
                    sourceFieldName: secondRelationItem.destinationFieldName,
                    sourceFieldValue: object[secondRelationItem.destinationFieldName]?.toString()
                }));
            } else {
                return relation.settings.items.map((relationItem) => ({
                    caseSensitive: relationItem.caseSensitive,
                    dataType: relationItem.dataType,
                    sourceFieldName: relationItem.sourceFieldName,
                    sourceFieldValue: object[relationItem.destinationFieldName]?.toString()
                }));
            }
        });

        // get unique values from array of arrays
        const set = new Set(objectData.map(JSON.stringify));
        let relationItems = Array.from(set).map(JSON.parse);

        // get related query parameters only in case of joining table
        if (relation.settings.relationTable) {
            const relatedWfsQueryParametersPromisses = relationItems.map((relationItem) =>
                dispatch('useApiData', {
                    name: 'GetRelatedWfsQueryParameters',
                    parameters: { Id: relation.id },
                    data: relationItem
                })
            );
            const relatedWfsQueryParametersResults = await Promise.all(
                relatedWfsQueryParametersPromisses
            );
            const relatedRelationItems = relatedWfsQueryParametersResults.flatMap((result, index) => {
                const relatedData = result?.data ? JSON.parse(result.data) : [];

                return relatedData.map((relationItem) => {
                    return relation.settings.items.map((attribute) => ({
                        caseSensitive: attribute.caseSensitive,
                        dataType: attribute.dataType,
                        sourceFieldName: attribute.sourceFieldName,
                        sourceFieldValue: relationItem[attribute.sourceFieldName]?.toString()
                    }));
                });
            });
            relationItems = [...relatedRelationItems];
        }

        // no relation items found
        if (!relationItems || relationItems.length === 0) {
            commit('ADD_STATUS_MESSAGE', {
                message: i18n.t('noResultsFound'),
                variant: 'warning',
                autoHideDelay: 5000
            });
            return;
        }

        // construct array with filters
        let filters = relationItems.map((relationItem) => {
            return relationItem.map((attribute) => ({
                caseSensitive: attribute.caseSensitive,
                filterAttribute: attribute.sourceFieldName,
                operator: '=',
                value: attribute.sourceFieldValue ?? null,
                visible: true,
                dataType: `xsd:${attribute.dataType}`
            }));
        });

        let multipleFilters = true;
        if (relationItems.length === 1) {
            filters = filters[0];
            multipleFilters = false;
        }

        // get data from relation
        const layerData = await dispatch('findDataRelation', {
            relation,
            filters,
            multipleFilters,
            showSelectionOnMap: !isAdvancedSearch
        });

        if (isAdvancedSearch) {
            dispatch('linkTableToLayerFeatures', {
                title,
                layerData,
                tableResultData: data,
                relation,
                layer
            });
        }

        // activate tab
        const group = getters.getSelectionGroups.findIndex(
            (item) => item.id === layer.id
        );
        commit('SET_SELECTION_POINTER', { group, index: -1 });
    },
    async linkTableToLayerFeatures({ commit, dispatch }, { title, layerData, tableResultData, relation, layer }) {
        // If a row in tableresults tab is clicked the related objects of the themelayer should be highlighted/zoomed
        // The following code controls linking the features to the tableresults
        if (layerData.length > 0 && layerData[0].features.length > 0) {
            // number of tableresults does not have to be equal to number of layer results
            // search for the corresponding features of the tableresult in layerData
            // use the keys from the relation for this
            const features = tableResultData
                .map((item) => {
                    let conditions = [];
                    // use all keys in relation
                    relation.settings.items.forEach((relationItem) => {
                        conditions.push((feature) => {
                            return feature.getProperties()[relationItem.sourceFieldName] === item[relationItem.destinationFieldName];
                        });
                    });
                    // find corresponding features
                    const features = layerData[0].features.filter((feature) => {
                        return conditions.every((condition) => {
                            return condition(feature);
                        });
                    });
                    // allways take the first one
                    return {
                        feature: features[0]
                    };
                })
                // use flatMap to not lose Feature data type
                .flatMap(({ feature }) => feature);

            // assign the features to tableresult
            // take index 0 for first tab
            commit('UPDATE_OBJECT_DATA_FEATURES', { index: 0, features });

            // add related objects to selection layer
            commit('ADD_SELECTION_OBJECT_DATA', {
                title,
                results: {
                    features,
                    computedStyles: layer.extended.computedStyles,
                }
            });

            // show related objects on map
            dispatch('displaySelectedItemObjectData', {
                groupTitle: title
            });
        }
    },
    async findTable2TableRelation({ getters, commit, dispatch }, { relation, data }) {
        commit('NEDGLOBE_MOBILE_DISPLAY_SELECTION_START');

        // Clear possible results for this title and open new tab with status Loading
        dispatch('initTabObjectSearch', { title: relation.name });


        try {
            const tableResult = await dispatch('useApiData', {
                name: getters.isMobile ? 'GetTableSearchResult' : 'GetPublicTableSearchResult',
                parameters: { Id: relation.toTableSearchResultId }
            });
            const tableSettings = { ...tableResult.data };

            // construct array with relations items and its data
            const objectData = data.map((object) => {
                return relation.settings.items.map((relationItem) => ({
                    caseSensitive: relationItem.caseSensitive,
                    dataType: relationItem.dataType,
                    sourceFieldName: relationItem.sourceFieldName,
                    sourceFieldValue: object[relationItem.sourceFieldName]?.toString()
                }));
            });

            const relatedTableData = await Promise.all(
                objectData.map((object) =>
                    dispatch('useApiData', {
                        name: getters.isMobile ? 'GetRelatedTableTableData' : 'GetPublicRelatedTableTableData',
                        parameters: { Id: relation.id },
                        data: object
                    })
                ));

            const tableData = relatedTableData
                .map((result) => {
                    const data = result.data && result.data.length > 0 ? JSON.parse(result.data) : [];
                    return {
                        data
                    };
                })
                .filter(({ data }) => data.length > 0);

            let fields = [];
            let maxResultsExceeded = false;
            let items = tableData.flatMap(({ data }) => data);

            if (items.length > 0) {
                maxResultsExceeded = items.length > MAX_RESULTS_RELATION;
                if (maxResultsExceeded) {
                    items = items.slice(0, MAX_RESULTS_RELATION);
                }
                items.forEach((item, index) => {
                    // provide a unique ID column to avoid conflicts with the ID column in the data
                    item.nedGlobeRowId = index;
                });
                fields = tableSettings.tableSearchResultFields
                    .filter(attribute => {
                        return displayThemeAttribute(attribute);
                    })
                    .map(attribute => (
                        {
                            ...attribute,
                            key: attribute.columnName,
                            label: attribute.alias,
                            sortable: true,
                        }
                    ))
                    .sort((a, b) => a.order - b.order);
            }
            commit('UPDATE_LOADING_OBJECT_DATA', {
                title: relation.name,
                order: relation.order,
                data: {
                    items,
                    fields,
                    selectable: true
                },
                message: maxResultsExceeded ? i18n.t('relations.maxResultsExceeded', [MAX_RESULTS_RELATION]) : '',
                count: items.length,
                exportable: true,
                download: true,
                printable: true,
                status: TAB_STATUS.READY
            });
        } catch (err) {
            commit('UPDATE_LOADING_OBJECT_DATA', {
                title: relation.name,
                order: relation.order,
                data: {
                    items: [],
                    fields: []
                },
                count: 0,
                status: TAB_STATUS.ERROR,
                message: err.message
            });
        }

        commit('NEDGLOBE_MOBILE_DISPLAY_SELECTION_END');
    }
};
